/**
 * Watchlist Button Component
 *
 * A toggle button for adding/removing items from the watchlist.
 * Shows a heart icon (filled when in watchlist, outline when not).
 */

import { useCallback } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWatchlist } from "@/hooks/useWatchlist";

// ============================================================================
// Types
// ============================================================================

/**
 * Props for the WatchlistButton component
 */
export interface WatchlistButtonProps {
  /** The ISIN of the fund to add/remove from watchlist */
  isin: string;
  /** Optional fund name for accessibility (used in aria-label) */
  fundName?: string;
  /** Button size variant */
  size?: "default" | "sm" | "lg" | "icon" | "icon-sm" | "icon-lg";
  /** Button variant (defaults to ghost for minimal visual weight) */
  variant?: "default" | "secondary" | "ghost" | "outline";
  /** Additional CSS classes */
  className?: string;
  /** Callback fired when watchlist state changes */
  onToggle?: (isin: string, isNowInWatchlist: boolean) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Show label alongside icon */
  showLabel?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Watchlist toggle button with heart icon
 *
 * Displays a heart icon that toggles between filled (in watchlist)
 * and outline (not in watchlist) states. Provides visual feedback
 * and proper accessibility.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <WatchlistButton isin="INF123456789" />
 *
 * // With fund name for better accessibility
 * <WatchlistButton
 *   isin="INF123456789"
 *   fundName="Parag Parikh Flexi Cap Fund"
 * />
 *
 * // With callback and label
 * <WatchlistButton
 *   isin="INF123456789"
 *   fundName="Parag Parikh Flexi Cap Fund"
 *   showLabel
 *   onToggle={(isin, isNowInWatchlist) => {
 *     console.log(`${isin} ${isNowInWatchlist ? 'added to' : 'removed from'} watchlist`);
 *   }}
 * />
 *
 * // Icon-only small button
 * <WatchlistButton
 *   isin="INF123456789"
 *   size="icon-sm"
 *   variant="ghost"
 * />
 * ```
 */
export function WatchlistButton({
  isin,
  fundName,
  size = "icon",
  variant = "ghost",
  className,
  onToggle,
  disabled = false,
  showLabel = false,
}: WatchlistButtonProps) {
  const { isInWatchlist, toggleWatchlist } = useWatchlist();
  const isWatched = isInWatchlist(isin);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Prevent click from bubbling to parent elements (e.g., card navigation)
      e.stopPropagation();
      e.preventDefault();

      const isNowInWatchlist = toggleWatchlist(isin);
      onToggle?.(isin, isNowInWatchlist);
    },
    [isin, toggleWatchlist, onToggle]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.stopPropagation();
        e.preventDefault();
        const isNowInWatchlist = toggleWatchlist(isin);
        onToggle?.(isin, isNowInWatchlist);
      }
    },
    [isin, toggleWatchlist, onToggle]
  );

  // Build accessible label
  const actionLabel = isWatched ? "Remove from watchlist" : "Add to watchlist";
  const ariaLabel = fundName
    ? `${actionLabel}: ${fundName}`
    : actionLabel;

  // Determine if we should use icon size (no label)
  const effectiveSize = showLabel ? size : size;

  return (
    <Button
      type="button"
      variant={variant}
      size={effectiveSize}
      className={cn(
        "transition-all duration-200",
        // Hover and active states
        isWatched
          ? "text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
          : "text-muted-foreground hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30",
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={isWatched}
      title={ariaLabel}
    >
      <Heart
        className={cn(
          "transition-all duration-200",
          isWatched && "fill-current",
          // Size adjustments based on button size
          showLabel ? "h-4 w-4" : ""
        )}
      />
      {showLabel && (
        <span className="ml-1">
          {isWatched ? "Watchlisted" : "Watchlist"}
        </span>
      )}
    </Button>
  );
}

// ============================================================================
// Variants
// ============================================================================

/**
 * Compact watchlist button for use in list items or cards
 */
export function WatchlistButtonCompact({
  className,
  ...props
}: Omit<WatchlistButtonProps, "size" | "showLabel">) {
  return (
    <WatchlistButton
      {...props}
      size="icon-sm"
      variant="ghost"
      className={cn("h-7 w-7", className)}
    />
  );
}

/**
 * Watchlist button with label for use in detail views or prominent placements
 */
export function WatchlistButtonWithLabel({
  className,
  ...props
}: Omit<WatchlistButtonProps, "showLabel">) {
  return (
    <WatchlistButton
      {...props}
      showLabel
      size="sm"
      variant="outline"
      className={className}
    />
  );
}

export default WatchlistButton;
